import { selectLiveTasks } from '@/store/notification/selectors';
import { useGetTaskSnapshotQuery } from '@/store/query/tasks';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { NavLink } from './NavLink';
import { CalendarCheck02 } from '@kula-ai/icons-core';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { useGetTasksMetricsQuery } from '@/store/query/dashboard';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { useKFlags } from '@/hooks/use-k-flags';
import { useRequisitionsEnabled } from '@/hooks/useRequisitionsEnabled';
import { useIdleReady } from '@/hooks/useIdleReady';

export const TaskNav = ({ isCollapsed }) => {
  const { pathname } = useLocation();
  const liveTasks = useSelector(selectLiveTasks);
  const { isFlowEnabled } = usePermissionGuard();
  const { isJobsVisible } = useAtsPermissions();
  const { ats_enabled } = useKFlags();
  const { isEnabled: isRequisitionsEnabled } = useRequisitionsEnabled();

  // Two requests for one count badge, and they fired during page load on every
  // route in the app. Sentry, 24h, settings routes alone: `/tasks/stats` 163
  // calls at p75 956ms and `/tasks/metrics` 163 at p75 1,108ms — 551k ms a day
  // spent in front of page content to render a number beside "Tasks". Waiting
  // for idle takes them off that path; the badge fills in a moment later.
  const isIdle = useIdleReady();

  const { data: tasks = {}, refetch } = useGetTaskSnapshotQuery(
    {},
    { skip: !isFlowEnabled || !isIdle }
  );
  const { data: jobTasksData = {} } = useGetTasksMetricsQuery(
    {},
    { skip: !isJobsVisible || !isIdle }
  );
  const { counts: jobTasksCount = {} } = jobTasksData;
  const {
    job_approval = 0,
    offer_approval = 0,
    scorecard = 0,
    requisition_approval = 0,
  } = jobTasksCount;

  const jobTaskCount =
    job_approval +
    offer_approval +
    scorecard +
    (isRequisitionsEnabled ? requisition_approval : 0);

  const pendingTasks =
    (tasks?.pending_counts?.today || 0) + (tasks?.pending_counts?.past || 0);

  const totalTasksCount = pendingTasks + jobTaskCount;

  useEffect(() => {
    // `refetch` on a skipped query is a no-op, so live task pushes that land
    // before idle would be dropped rather than queued. Once idle latches true
    // the query mounts and fetches anyway, which covers exactly that window.
    if (!isFlowEnabled || !isIdle) return;
    refetch();
  }, [liveTasks, isIdle]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <NavLink
      link={ats_enabled ? '/tasks/jobs/scorecard' : '/tasks'}
      count={totalTasksCount}
      title="Tasks"
      icon={CalendarCheck02}
      isCollapsed={isCollapsed}
      // Stay active across all /tasks sub-tabs, not just the Jobs sub-route.
      isCustomActive={pathname.startsWith('/tasks')}
    />
  );
};
